import { useStyle } from '@/classify';
import { useProductImageCarousel } from '@/hooks/ProductImageCarousel/useProductImageCarousel';
import { capitalizeFirstLetter } from '@/lib/format-string';
import { transparentPlaceholder } from '@/util/images';
import Image from 'next/image';
import { useMemo, useRef } from 'react';
import { ChevronLeft as ArrowLeftIcon, ChevronRight as ArrowRightIcon } from 'react-feather';
import { useIntl } from 'react-intl';
import SlickSlider from 'react-slick';
import AriaButton from '../AriaButton';
import Icon from '../Icon';
// import Image from '../Image';
import SimpleImage from '../Image/simpleImage';
import defaultClasses from './carousel.module.css';
import Thumbnail from './thumbnail';
const IMAGE_WIDTH = 640;

export type ImagesType = {
  uid: string;
  disabled: boolean;
  file: string;
  label: any;
  position: number;
  url: string;
  __typename: string;
};

export const ProductImageCarousel = ({
  images,
  name,
  sku,
  seoColorLabel,
}: {
  images: ImagesType[];
  name: string;
  sku: string;
  seoColorLabel: string;
}) => {
  const { formatMessage } = useIntl();

  const containerRef = useRef<HTMLDivElement>(null);
  const thumbnailListRef = useRef<HTMLDivElement>(null);

  const classes = useStyle(defaultClasses);
  const {
    currentImage,
    activeItemIndex,
    handleNext,
    handlePrevious,
    handleThumbnailClick,
    sortedImages,
  }: {
    currentImage: ImagesType;
    activeItemIndex: number;
    handleNext: () => void;
    handlePrevious: () => void;
    handleThumbnailClick: (index: number) => void;
    sortedImages: ImagesType[];
  } = useProductImageCarousel({
    images,
    imageWidth: IMAGE_WIDTH,
  });

  const previousButton = formatMessage({
    id: 'productImageCarousel.previousButtonAriaLabel',
    defaultMessage: 'Previous Image',
  });
  const nextButton = formatMessage({
    id: 'productImageCarousel.nextButtonAriaLabel',
    defaultMessage: 'Next Image',
  });
  const chevronClasses = {
    root: classes.chevron,
  };

  // create thumbnail image component for every images in sorted order
  const thumbnails = useMemo(
    () =>
      sortedImages.map((item, index) => (
        <Thumbnail
          alt={`${capitalizeFirstLetter(seoColorLabel)} ${name} for ${sku} image ${index}`}
          title={`${capitalizeFirstLetter(seoColorLabel)} ${name} for ${sku} image ${index}`}
          key={item.uid}
          item={item}
          itemIndex={index}
          isActive={activeItemIndex === index}
          onClickHandler={handleThumbnailClick}
        />
      )),
    [seoColorLabel, activeItemIndex, handleThumbnailClick, name, sku, sortedImages],
  );

  const thumbnailList = useMemo(() => {
    let slidesToShow = 4;

    if (window.innerWidth < 1200) {
      slidesToShow = 2;
    } else if (window.innerWidth < 1600) {
      slidesToShow = 3;
    }

    const sliderProps = {
      fade: false,
      infinite: false,
      showArrows: false,
      dots: false,
      arrows: false,
      vertical: false,
      verticalSwiping: false,
      slidesToShow,
      nextArrow: <SampleNextArrow />,
      prevArrow: <SamplePrevArrow />,
    };

    function SampleNextArrow({ className, style, onClick }: { className?: string; style?: any; onClick?: any }) {
      return (
        <div
          className={className}
          style={{
            ...style,
          }}
          onClick={onClick}
        />
      );
    }

    function SamplePrevArrow({ className, style, onClick }: { className?: string; style?: any; onClick?: any }) {
      return (
        <div
          className={className}
          style={{
            ...style,
          }}
          onClick={onClick}
        />
      );
    }

    return (
      <div
        className={classes.thumbnailList}
        ref={thumbnailListRef}
      >
        <SlickSlider {...sliderProps}>{thumbnails}</SlickSlider>
      </div>
    );
  }, [classes.thumbnailList, thumbnails]);

  // useEffect(() => {
  //   const handleResize = () => {
  //     if (
  //       thumbnailListRef?.current &&
  //       containerRef?.current
  //     ) {
  //       containerRef.current.style.width =
  //         thumbnailListRef.current.offsetHeight + "px";
  //       containerRef.current.style.height =
  //         thumbnailListRef.current.offsetHeight + "px";
  //     }
  //   };

  //   // 添加事件监听器
  //   window.addEventListener("resize", handleResize);

  //   // 初始调用一次设置初始尺寸
  //   handleResize();

  //   // 清理函数
  //   return () => {
  //     window.removeEventListener("resize", handleResize);
  //   };
  // }, []);

  return (
    <div className={classes.root}>
      <div
        className={classes.carouselContainer}
        ref={containerRef}
      >
        <AriaButton
          className={classes.previousButton}
          onPress={handlePrevious}
          aria-label={previousButton}
          type='button'
        >
          <Icon
            classes={chevronClasses}
            src={ArrowLeftIcon}
            size={28}
          />
        </AriaButton>
        <ImageComponent
          currentImage={currentImage}
          sku={sku}
          name={name}
          seoColorLabel={seoColorLabel}
        />
        <AriaButton
          className={classes.nextButton}
          onPress={handleNext}
          aria-label={nextButton}
          type='button'
        >
          <Icon
            classes={chevronClasses}
            src={ArrowRightIcon}
            size={28}
          />
        </AriaButton>
      </div>
      {thumbnailList}
    </div>
  );
};

function ImageComponent({
  currentImage,
  sku,
  name,
  seoColorLabel,
}: {
  currentImage: ImagesType;
  sku: string;
  name: string;
  seoColorLabel: string;
}) {
  const altText = `${capitalizeFirstLetter(seoColorLabel)} ${name} for ${sku} image ${currentImage.position}`;

  const classes = useStyle(defaultClasses);

  if (currentImage.url) {
    return (
      <div className='bg-[f9f9f9]'>
        <Image
          src={currentImage.url}
          width={1265}
          height={1600}
          alt={altText}
          title={altText}
          loader={({ src }) => src}
          className='h-auto w-full cursor-pointer object-cover'
          unoptimized
        />
        {/* <Image
          alt={altText}
          title={altText}
          resource={currentImage.url}
          width={1200}
          height={1200}
          unoptimized
        /> */}
      </div>
    );
  }

  return (
    <SimpleImage
      alt={altText}
      title={altText}
      src={transparentPlaceholder}
      width={1265}
      height={1600}
    />
  );
}
